import { useState, useEffect, useCallback } from "react";
import AdvancedFilterBar, { applyAdvancedFilters } from "../components/AdvancedFilterBar";
import DepensesFormDialog from "../components/DepensesFormDialog";
import DepensesImportDialog from "../components/DepensesImportDialog";
import { CompareToggle, Delta, AnomaliesAlert } from "../components/CompareToggle";
import PageHeader from "../components/PageHeader";
import { TableSkeleton } from "../components/Skeletons";
import { useAuth } from "../contexts/AuthContext";
import { useSociete } from "../contexts/SocieteContext";
import {
  Wallet, Plus, Loader2, Check, X, Filter, Calendar, ChevronDown,
  AlertTriangle, Pencil, Trash2, CheckCircle2, Clock, TrendingUp,
  TrendingDown, DollarSign, BarChart3, RefreshCw, ChevronLeft, ChevronRight,
  Upload, FileSpreadsheet, ArrowRight, Settings2, Eye, EyeOff, Search, GripVertical, Paperclip
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend, Area, AreaChart } from "recharts";

const STATUS_COLORS = {
  solde: { bg: "#2563eb20", text: "#3b82f6", label: "Solde" },
  paye: { bg: "#06b6d420", text: "#06b6d4", label: "Paye" },
  a_echeance: { bg: "#f59e0b20", text: "#f59e0b", label: "A echeance" },
  creance: { bg: "#ef444420", text: "#ef4444", label: "Creance" },
  appurement: { bg: "#ec489920", text: "#ec4899", label: "Appurement" },
  en_attente: { bg: "#6b728020", text: "#9ca3af", label: "En attente" },
  litige: { bg: "#f9731620", text: "#f97316", label: "Litige" },
  urgence: { bg: "#dc262620", text: "#dc2626", label: "Urgence" },
  en_retard: { bg: "#b9130020", text: "#dc2626", label: "En retard" },
  partiel: { bg: "#8b5cf620", text: "#8b5cf6", label: "Partiel" },
  suspendu: { bg: "#78716c20", text: "#a8a29e", label: "Suspendu" },
};

const fmt = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);

export default function DepensesPage() {
  const { axiosAuth } = useAuth();
  const { societeApi, currentSociete } = useSociete();
  const api = societeApi();

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [config, setConfig] = useState({ categories: [], statuses: [] });
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date().toISOString().slice(0, 7));
  const [filterCat, setFilterCat] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("table");
  const [showImport, setShowImport] = useState(false);
  const [classeurs, setClasseurs] = useState([]);
  const [filterClasseur, setFilterClasseur] = useState("");
  const [showClasseurMgr, setShowClasseurMgr] = useState(false);
  const [newClasseur, setNewClasseur] = useState({ name: "", color: "#f7c935", description: "" });
  const [showConfig, setShowConfig] = useState(false);
  const [advFilters, setAdvFilters] = useState([]);
  const [filterLogic, setFilterLogic] = useState("AND");
  const [compareYoY, setCompareYoY] = useState(false);
  const [form, setForm] = useState({
    element: "", category: "divers", status: "en_attente", echeance: "",
    solde_ttc: "", modalite_paiement: "", compte: "", facture_ref: "",
    notes: "", appurement_mensuel: "", appurement_nb_mois: "",
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, summaryRes, monthsRes, configRes, classRes] = await Promise.all([
        api.get(`/depenses/list?month=${currentMonth}&category=${filterCat}&status=${filterStatus}`),
        api.get(`/depenses/summary?month=${currentMonth}${compareYoY ? "&compare=year" : ""}`),
        api.get("/depenses/months"),
        api.get("/depenses/config"),
        api.get("/depenses/classeurs"),
      ]);
      setItems(itemsRes.data || []);
      setSummary(summaryRes.data || null);
      setMonths(monthsRes.data || []);
      setConfig(configRes.data || { categories: [], statuses: [], columns: [], banques: [], moyens_paiement: [] });
      setClasseurs(classRes.data || []);
    } catch (e) { console.error("Depenses load error:", e); }
    setLoading(false);
  }, [currentMonth, filterCat, filterStatus, compareYoY, currentSociete]);

  useEffect(() => { loadData(); }, [loadData]);

  const saveClasseur = async () => {
    if (!newClasseur.name.trim()) return;
    try {
      await api.post("/depenses/classeurs", newClasseur);
      setNewClasseur({ name: "", color: "#f7c935", description: "" });
      loadData();
    } catch { /* ignore */ }
  };
  const deleteClasseur = async (name) => {
    try { await api.delete(`/depenses/classeurs/${encodeURIComponent(name)}`); loadData(); } catch { /* ignore */ }
  };

  const resetForm = () => {
    setForm({ element: "", category: "divers", status: "en_attente", echeance: "", solde_ttc: "", modalite_paiement: "", compte: "", facture_ref: "", notes: "", classeur: "", appurement_mensuel: "", appurement_nb_mois: "", justificatif: null, _pendingFile: null, _pendingFileName: null });
    setEditItem(null);
    setShowForm(false);
  };

  const startEdit = (item) => {
    setForm({
      element: item.element || "", category: item.category || "divers", status: item.status || "en_attente",
      echeance: item.echeance || "", solde_ttc: item.solde_ttc || "", modalite_paiement: item.modalite_paiement || "",
      compte: item.compte || "", facture_ref: item.facture_ref || "", notes: item.notes || "", classeur: item.classeur || "",
      appurement_mensuel: item.appurement_mensuel || "", appurement_nb_mois: item.appurement_nb_mois || "",
      justificatif: item.justificatif || null, _pendingFile: null, _pendingFileName: null,
    });
    setEditItem(item.depense_id);
    setShowForm(true);
  };

  const saveItem = async () => {
    if (!form.element.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form, solde_ttc: parseFloat(form.solde_ttc) || 0, period: currentMonth };
      delete payload._pendingFile;
      delete payload._pendingFileName;
      let depenseId = editItem;
      if (editItem) {
        await api.put(`/depenses/${editItem}`, payload);
      } else {
        const r = await api.post("/depenses/create", payload);
        depenseId = r.data?.depense_id;
      }
      // Upload pending file if any
      if (form._pendingFile && depenseId) {
        const fd = new FormData();
        fd.append("file", form._pendingFile);
        fd.append("depense_id", depenseId);
        await api.post("/depenses/upload-justificatif", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      resetForm();
      loadData();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const deleteItem = async (id) => {
    try { await api.delete(`/depenses/${id}`); loadData(); } catch { /* ignore */ }
  };

  const pointerItem = async (id, val) => {
    try { await api.post(`/depenses/pointer/${id}`, { pointage: val }); loadData(); } catch { /* ignore */ }
  };

  const updateField = async (id, field, val) => {
    try { await api.put(`/depenses/${id}`, { [field]: val }); loadData(); } catch { /* ignore */ }
  };

  const changeMonth = (dir) => {
    const d = new Date(currentMonth + "-01");
    d.setMonth(d.getMonth() + dir);
    setCurrentMonth(d.toISOString().slice(0, 7));
  };

  const monthLabel = (m) => {
    const d = new Date(m + "-01");
    return d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  };

  // Filter items
  const filtered = applyAdvancedFilters(
    filterClasseur ? items.filter(i => i.classeur === filterClasseur) : items,
    advFilters, filterLogic, config
  );

  const catLabel = (key) => config.categories?.find(c => c.key === key)?.label || key;

  const getStatusStyle = (key) => {
    const fromConfig = config.statuses?.find(s => s.key === key);
    if (fromConfig?.color) {
      return { bg: fromConfig.color + "20", text: fromConfig.color, label: fromConfig.label || key };
    }
    return STATUS_COLORS[key] || { bg: "#6b728020", text: "#9ca3af", label: key };
  };

  const visibleCols = (config.columns || []).filter(c => c.visible);

  const renderCell = (item, col) => {
    const val = item[col.key];
    switch (col.type) {
      case "currency":
        return <span className="text-xs font-bold" style={{ color: "#f7c935" }}>{fmt(val)}</span>;
      case "date":
        return <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>{val || "-"}</span>;
      case "checkbox":
        return <input type="checkbox" checked={!!val} onChange={() => updateField(item.depense_id, col.key, !val)} className="w-4 h-4 accent-[#f7c935] cursor-pointer" />;
      case "select": {
        if (col.source === "statuses") {
          const sc = getStatusStyle(val);
          return <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: sc.bg, color: sc.text }}>{sc.label}</span>;
        }
        if (col.source === "categories") return <span className="text-xs" style={{ color: "var(--text-muted)" }}>{catLabel(val)}</span>;
        const list = config[col.source] || [];
        const found = list.find(x => x.key === val);
        return <span className="text-xs" style={{ color: "var(--text-primary)" }}>{found?.label || val || "-"}</span>;
      }
      default:
        return <span className="text-xs" style={{ color: "var(--text-primary)" }}>{val || "-"}</span>;
    }
  };

  return (
    <div className="p-6 max-w-full" data-testid="depenses-page">
      <PageHeader
        icon={<Wallet />}
        title="Dépenses"
        description="Suivi des charges, créances, encours et apurements"
        testId="depenses-header"
        actions={(
          <>
            <Button onClick={() => setShowImport(true)} variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs h-9" data-testid="import-depense-btn">
              <Upload className="w-3.5 h-3.5 mr-1.5" /> Importer
            </Button>
            <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold text-xs h-9" data-testid="add-depense-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Nouvelle depense
            </Button>
            <button onClick={() => setShowConfig(true)} className="p-2 rounded-lg hover:bg-white/5" data-testid="dep-config-btn">
              <Settings2 className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
            </button>
          </>
        )}
      />

      {/* Month Navigator + Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex items-center gap-1 rounded-xl border px-1 py-1" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }}>
          <button onClick={() => changeMonth(-1)} data-testid="dep-month-prev" className="p-1.5 rounded-lg hover:bg-white/5"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-bold px-3 capitalize" data-testid="current-month">{monthLabel(currentMonth)}</span>
          <button onClick={() => changeMonth(1)} data-testid="dep-month-next" className="p-1.5 rounded-lg hover:bg-white/5"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <Select value={filterCat || "__all__"} onValueChange={v => setFilterCat(v === "__all__" ? "" : v)}>
          <SelectTrigger data-testid="filter-category" className="h-9 w-auto min-w-[160px] bg-transparent border text-xs" style={{ borderColor: "var(--border-primary)", color: "var(--text-primary)" }}>
            <SelectValue placeholder="Toutes categories" />
          </SelectTrigger>
          <SelectContent className="bg-[#14101c] border-white/10 text-white max-h-72">
            <SelectItem value="__all__" className="text-xs text-slate-200 focus:bg-white/10 focus:text-white" data-testid="filter-category-option-all">Toutes categories</SelectItem>
            {config.categories.map(c => (
              <SelectItem key={c.key} value={c.key} className="text-xs text-slate-200 focus:bg-white/10 focus:text-white" data-testid={`filter-category-option-${c.key}`}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus || "__all__"} onValueChange={v => setFilterStatus(v === "__all__" ? "" : v)}>
          <SelectTrigger data-testid="filter-status" className="h-9 w-auto min-w-[140px] bg-transparent border text-xs" style={{ borderColor: "var(--border-primary)", color: "var(--text-primary)" }}>
            <SelectValue placeholder="Tous statuts" />
          </SelectTrigger>
          <SelectContent className="bg-[#14101c] border-white/10 text-white max-h-72">
            <SelectItem value="__all__" className="text-xs text-slate-200 focus:bg-white/10 focus:text-white" data-testid="filter-status-option-all">Tous statuts</SelectItem>
            {config.statuses.map(s => (
              <SelectItem key={s.key} value={s.key} className="text-xs text-slate-200 focus:bg-white/10 focus:text-white" data-testid={`filter-status-option-${s.key}`}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterClasseur || "__all__"} onValueChange={v => setFilterClasseur(v === "__all__" ? "" : v)}>
          <SelectTrigger data-testid="filter-classeur" className="h-9 w-auto min-w-[150px] bg-transparent border text-xs" style={{ borderColor: "var(--border-primary)", color: "var(--text-primary)" }}>
            <SelectValue placeholder="Tous classeurs" />
          </SelectTrigger>
          <SelectContent className="bg-[#14101c] border-white/10 text-white max-h-72">
            <SelectItem value="__all__" className="text-xs text-slate-200 focus:bg-white/10 focus:text-white" data-testid="filter-classeur-option-all">Tous classeurs</SelectItem>
            {classeurs.map(c => (
              <SelectItem key={c.name} value={c.name} className="text-xs text-slate-200 focus:bg-white/10 focus:text-white" data-testid={`filter-classeur-option-${c.name}`}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={() => setShowClasseurMgr(!showClasseurMgr)} variant="outline" className="h-9 px-3 text-xs font-medium bg-transparent" style={{ borderColor: "var(--border-primary)", color: "var(--text-muted)" }} data-testid="manage-classeurs-btn">
          Classeurs
        </Button>
        <div className="flex items-center gap-1 rounded-lg border px-1 py-1 ml-auto" style={{ borderColor: "var(--border-primary)" }}>
          {[{k:"table",l:"Tableau"},{k:"summary",l:"Synthese"}].map(v => (
            <button key={v.k} onClick={() => setView(v.k)} data-testid={`view-${v.k}`}
              className={`px-4 py-1.5 rounded-md text-[11px] font-bold transition-colors ${view === v.k ? "text-[#0b0811]" : ""}`}
              style={view === v.k ? { background: "#f7c935" } : { color: "var(--text-muted)" }}>
              {v.l}
            </button>
          ))}
        </div>
      </div>

      {/* Advanced Filter Bar */}
      <AdvancedFilterBar columns={config.columns || []} config={config} filters={advFilters} setFilters={setAdvFilters} logic={filterLogic} setLogic={setFilterLogic} />

      {/* Classeur Manager */}
      {showClasseurMgr && (
        <div className="rounded-xl border p-4 mb-6" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} data-testid="classeur-manager">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>Gestion des classeurs</h3>
            <button onClick={() => setShowClasseurMgr(false)} className="p-1 hover:bg-white/10 rounded"><X className="w-4 h-4" style={{ color: "var(--text-muted)" }} /></button>
          </div>
          <div className="flex gap-2 mb-3">
            <Input value={newClasseur.name} onChange={e => setNewClasseur({ ...newClasseur, name: e.target.value })} placeholder="Nom du classeur"
              className="bg-[#0b0811] border-white/10 text-white text-xs h-8 flex-1" data-testid="new-classeur-name" />
            <input type="color" value={newClasseur.color} onChange={e => setNewClasseur({ ...newClasseur, color: e.target.value })}
              className="w-8 h-8 rounded border-0 cursor-pointer bg-transparent" />
            <Input value={newClasseur.description} onChange={e => setNewClasseur({ ...newClasseur, description: e.target.value })} placeholder="Description (optionnel)"
              className="bg-[#0b0811] border-white/10 text-white text-xs h-8 w-48" />
            <Button onClick={saveClasseur} disabled={!newClasseur.name.trim()} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold text-xs h-8" data-testid="save-classeur-btn">
              <Plus className="w-3 h-3 mr-1" /> Ajouter
            </Button>
          </div>
          {classeurs.length > 0 ? (
            <div className="space-y-1">
              {classeurs.map(c => (
                <div key={c.name} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/[0.02]" style={{ borderLeft: `3px solid ${c.color || "#f7c935"}` }}>
                  <span className="text-xs font-medium flex-1" style={{ color: "var(--text-primary)" }}>{c.name}</span>
                  {c.description && <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>{c.description}</span>}
                  <button onClick={() => deleteClasseur(c.name)} className="p-1 hover:bg-red-500/10 rounded"><Trash2 className="w-3 h-3 text-red-400" /></button>
                </div>
              ))}
            </div>
          ) : <p className="text-xs text-center py-3" style={{ color: "var(--text-muted)" }}>Aucun classeur. Creez-en un ci-dessus.</p>}
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: "Total TTC", value: summary.totals.total_ttc, icon: DollarSign, color: "#f7c935" },
            { label: "Paye / Solde", value: summary.totals.total_paye, icon: CheckCircle2, color: "#22c55e" },
            { label: "Creances", value: summary.totals.total_creance, icon: AlertTriangle, color: "#ef4444" },
            { label: "Encours", value: summary.totals.total_encours, icon: Clock, color: "#f59e0b" },
          ].map(card => (
            <div key={card.label} className="rounded-xl border p-4" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }}>
              <div className="flex items-center gap-2 mb-2">
                <card.icon className="w-4 h-4" style={{ color: card.color }} />
                <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{card.label}</span>
              </div>
              <div className="text-xl font-black" style={{ color: card.color }}>{fmt(card.value)}</div>
              <div className="text-[10px] mt-0.5" style={{ color: "var(--text-dimmed)" }}>{summary.totals.count} depense(s)</div>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <TableSkeleton rows={6} cols={5} testId="depenses-loading-skeleton" />
      ) : view === "summary" ? (
        /* Summary by category */
        <div className="space-y-4">
          {compareYoY && summary?.anomalies && summary.anomalies.length > 0 && (
            <AnomaliesAlert anomalies={summary.anomalies} testId="depenses-anomalies" />
          )}
          <div className="rounded-xl border p-6" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} data-testid="summary-view">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <h2 className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>Synthese par categorie — {monthLabel(currentMonth)}</h2>
            <div className="flex items-center gap-2">
              <CompareToggle enabled={compareYoY} onChange={setCompareYoY} testId="depenses-compare-yoy" />
              {compareYoY && summary?.prev?.totals && (
                <span className="text-[10px] font-bold tabular-nums" style={{ color: "var(--text-muted)" }} data-testid="depenses-yoy-total">
                  N-1: {fmt(summary.prev.totals.total_ttc)}
                  <span className="ml-2"><Delta current={summary.totals.total_ttc} previous={summary.prev.totals.total_ttc} inverted testId="depenses-yoy-total-delta" /></span>
                </span>
              )}
            </div>
          </div>
          {summary?.by_category?.length > 0 ? (
            <div className="space-y-2">
              {summary.by_category.map(cat => {
                const prevCat = summary.prev?.by_category?.find(c => c.category === cat.category);
                return (
                  <div key={cat.category} className="flex items-center gap-4 p-3 rounded-lg hover:bg-white/[0.02]">
                    <div className="w-40 text-sm font-medium truncate" style={{ color: "var(--text-primary)" }}>{cat.label}</div>
                    <div className="flex-1">
                      <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                        <div className="h-full rounded-full bg-[#f7c935]" style={{ width: `${summary.totals.total_ttc > 0 ? (cat.total_ttc / summary.totals.total_ttc) * 100 : 0}%` }} />
                      </div>
                    </div>
                    <div className="text-sm font-bold w-28 text-right" style={{ color: "var(--text-primary)" }}>{fmt(cat.total_ttc)}</div>
                    {compareYoY && (
                      <div className="w-20 text-right" data-testid={`depenses-yoy-cat-${cat.category}`}>
                        <Delta current={cat.total_ttc} previous={prevCat?.total_ttc || 0} inverted />
                      </div>
                    )}
                    <div className="text-[10px] w-20 text-right" style={{ color: "var(--text-muted)" }}>{cat.count} items</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-center py-8" style={{ color: "var(--text-muted)" }}>Aucune depense ce mois-ci.</p>
          )}
          {summary?.by_status?.length > 0 && (
            <div className="mt-6 pt-4 border-t" style={{ borderColor: "var(--border-primary)" }}>
              <h3 className="text-xs font-bold mb-3" style={{ color: "var(--text-muted)" }}>Repartition par statut</h3>
              <div className="flex flex-wrap gap-2">
                {summary.by_status.map(st => {
                  const sc = getStatusStyle(st.status);
                  return (
                    <div key={st.status} className="rounded-lg px-3 py-2 text-center" style={{ background: sc.bg }}>
                      <div className="text-xs font-bold" style={{ color: sc.text }}>{fmt(st.total)}</div>
                      <div className="text-[9px] font-medium" style={{ color: sc.text }}>{sc.label} ({st.count})</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        </div>
      ) : (
        /* Table view - flat dynamic columns */
        <div className="space-y-4" data-testid="table-view">
          <div className="rounded-xl border overflow-hidden" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b" style={{ borderColor: "var(--border-primary)" }}>
                    {visibleCols.map(col => (
                      <th key={col.key} className={`px-3 py-2.5 text-[10px] font-medium uppercase tracking-wider ${col.type === "currency" ? "text-right" : col.type === "checkbox" ? "text-center" : "text-left"}`} style={{ color: "var(--text-muted)" }}>
                        {col.label}
                      </th>
                    ))}
                    <th className="px-3 py-2.5 text-[10px] font-medium uppercase tracking-wider text-right" style={{ color: "var(--text-muted)" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={visibleCols.length + 1} className="text-center py-12">
                      <Wallet className="w-8 h-8 mx-auto mb-2" style={{ color: "var(--text-dimmed)" }} />
                      <p className="text-sm" style={{ color: "var(--text-primary)" }}>Aucune depense pour {monthLabel(currentMonth)}</p>
                    </td></tr>
                  ) : filtered.map(item => (
                    <tr key={item.depense_id} className="border-b last:border-0 hover:bg-white/[0.02] transition-colors" style={{ borderColor: "var(--border-primary)" }} data-testid={`row-${item.depense_id}`}>
                      {visibleCols.map(col => (
                        <td key={col.key} className={`px-3 py-2.5 ${col.type === "currency" ? "text-right" : col.type === "checkbox" ? "text-center" : ""}`}>
                          {renderCell(item, col)}
                        </td>
                      ))}
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {item.justificatif?.url && (
                            <a href={item.justificatif.url} target="_blank" rel="noopener noreferrer"
                              className="p-1.5 rounded-md hover:bg-blue-500/10" title="Voir justificatif" data-testid={`justif-${item.depense_id}`}>
                              <Paperclip className="w-3.5 h-3.5 text-blue-400" />
                            </a>
                          )}
                          <button onClick={() => startEdit(item)} className="p-1.5 rounded-md hover:bg-white/10" data-testid={`edit-${item.depense_id}`}>
                            <Pencil className="w-3.5 h-3.5" style={{ color: "var(--text-muted)" }} />
                          </button>
                          <button onClick={() => deleteItem(item.depense_id)} className="p-1.5 rounded-md hover:bg-red-500/10" data-testid={`delete-${item.depense_id}`}>
                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <DepensesFormDialog
        open={showForm} onClose={resetForm} editItem={editItem ? { depense_id: editItem } : null}
        form={form} setForm={setForm} onSave={saveItem} saving={saving}
        config={config} classeurs={classeurs} api={api}
      />

      {/* Import Dialog */}
      <DepensesImportDialog
        open={showImport} onClose={() => setShowImport(false)}
        api={api} config={config} currentMonth={currentMonth}
        onImported={() => loadData()}
      />

      {/* Config Dialog */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg max-h-[80vh] overflow-y-auto" data-testid="dep-config-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Configuration Depenses</DialogTitle>
          </DialogHeader>
          <DepensesConfigPanel config={config} api={api} onSave={() => { setShowConfig(false); loadData(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DepensesConfigPanel({ config, api, onSave }) {
  const [categories, setCategories] = useState(config.categories || []);
  const [statuses, setStatuses] = useState(config.statuses || []);
  const [columns, setColumns] = useState(config.columns || []);
  const [banques, setBanques] = useState(config.banques || []);
  const [moyens, setMoyens] = useState(config.moyens_paiement || []);
  const [newCat, setNewCat] = useState("");
  const [newStat, setNewStat] = useState("");
  const [newColName, setNewColName] = useState("");
  const [newColType, setNewColType] = useState("text");
  const [newBanque, setNewBanque] = useState("");
  const [newMoyen, setNewMoyen] = useState("");
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const addCat = () => { if (newCat.trim()) { setCategories([...categories, { key: newCat.trim().toLowerCase().replace(/[^a-z0-9]/g, "_"), label: newCat.trim() }]); setNewCat(""); } };
  const removeCat = (i) => setCategories(categories.filter((_, idx) => idx !== i));
  const addStat = () => { if (newStat.trim()) { setStatuses([...statuses, { key: newStat.trim().toLowerCase().replace(/[^a-z0-9]/g, "_"), label: newStat.trim(), color: "#" + Math.floor(Math.random()*16777215).toString(16).padStart(6,"0") }]); setNewStat(""); } };
  const removeStat = (i) => setStatuses(statuses.filter((_, idx) => idx !== i));
  const toggleCol = (i) => { const c = [...columns]; c[i] = {...c[i], visible: !c[i].visible}; setColumns(c); };
  const removeCol = (i) => setColumns(columns.filter((_, idx) => idx !== i));
  const addCol = () => { if (newColName.trim()) { setColumns([...columns, { key: newColName.trim().toLowerCase().replace(/[^a-z0-9]/g,"_"), label: newColName.trim(), type: newColType, visible: true }]); setNewColName(""); setNewColType("text"); } };
  const addBanque = () => { if (newBanque.trim()) { setBanques([...banques, { key: newBanque.trim().toLowerCase().replace(/[^a-z0-9]/g,"_"), label: newBanque.trim() }]); setNewBanque(""); } };
  const removeBanque = (i) => setBanques(banques.filter((_, idx) => idx !== i));
  const addMoyen = () => { if (newMoyen.trim()) { setMoyens([...moyens, { key: newMoyen.trim().toLowerCase().replace(/[^a-z0-9]/g,"_"), label: newMoyen.trim() }]); setNewMoyen(""); } };
  const removeMoyen = (i) => setMoyens(moyens.filter((_, idx) => idx !== i));

  const handleDrop = (dropIdx) => {
    if (dragIdx === null || dragIdx === dropIdx) { setDragIdx(null); setDragOverIdx(null); return; }
    const reordered = [...columns];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    setColumns(reordered);
    setDragIdx(null); setDragOverIdx(null);
  };

  const save = async () => { setSaving(true); try { await api.put("/depenses/config", { categories, statuses, columns, banques, moyens_paiement: moyens }); onSave(); } catch {} setSaving(false); };

  const CT = { text: "Texte", number: "Nombre", currency: "Montant", date: "Date", checkbox: "Case", select: "Liste" };

  const ConfigList = ({ items, onRemove, addVal, setAddVal, onAdd, placeholder, showColor }) => (
    <div>
      <div className="space-y-1 mb-2 max-h-36 overflow-y-auto">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-[#0b0811]">
            {showColor && <div className="w-3 h-3 rounded-full flex-shrink-0" style={{background: item.color || "#94a3b8"}} />}
            <span className="text-xs flex-1" style={{color:"var(--text-primary)"}}>{item.label}</span>
            <button onClick={() => onRemove(i)} className="p-0.5 hover:bg-red-500/10 rounded"><X className="w-3 h-3 text-red-400" /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={addVal} onChange={e => setAddVal(e.target.value)} onKeyDown={e => e.key === "Enter" && onAdd()} placeholder={placeholder} className="bg-[#0b0811] border-white/10 text-white text-xs h-8 flex-1" />
        <Button onClick={onAdd} disabled={!addVal.trim()} className="bg-[#f7c935] text-[#0b0811] font-bold text-xs h-8"><Plus className="w-3 h-3" /></Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-5 mt-3">
      <div>
        <h3 className="text-xs font-bold mb-2" style={{ color: "#f7c935" }}>Colonnes du tableau</h3>
        <div className="space-y-1 mb-2 max-h-44 overflow-y-auto">
          {columns.map((col, i) => (
            <div key={col.key + i} draggable onDragStart={() => setDragIdx(i)} onDragOver={(e) => { e.preventDefault(); setDragOverIdx(i); }} onDrop={() => handleDrop(i)} onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all ${dragOverIdx === i ? "ring-1 ring-[#f7c935]/50 bg-[#f7c935]/5" : "bg-[#0b0811]"} ${dragIdx === i ? "opacity-40" : ""}`}
              style={{ cursor: "grab" }} data-testid={`col-row-${col.key}`}>
              <GripVertical className="w-3.5 h-3.5 text-slate-600 flex-shrink-0 cursor-grab" />
              <button onClick={() => toggleCol(i)} className="p-0.5 flex-shrink-0">{col.visible ? <Eye className="w-3.5 h-3.5 text-green-400" /> : <EyeOff className="w-3.5 h-3.5 text-slate-500" />}</button>
              <span className="text-xs flex-1" style={{ color: col.visible ? "var(--text-primary)" : "var(--text-dimmed)" }}>{col.label}</span>
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-slate-500">{CT[col.type] || col.type}</span>
              {!col.fixed && <button onClick={() => removeCol(i)} className="p-0.5 hover:bg-red-500/10 rounded"><X className="w-3 h-3 text-red-400" /></button>}
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input value={newColName} onChange={e => setNewColName(e.target.value)} onKeyDown={e => e.key === "Enter" && addCol()} placeholder="Nom colonne" className="bg-[#0b0811] border-white/10 text-white text-xs h-8 flex-1" />
          <select value={newColType} onChange={e => setNewColType(e.target.value)} className="bg-[#0b0811] border border-white/10 rounded-md text-xs h-8 px-2 text-white">{Object.entries(CT).map(([k,v]) => <option key={k} value={k}>{v}</option>)}</select>
          <Button onClick={addCol} disabled={!newColName.trim()} className="bg-[#f7c935] text-[#0b0811] font-bold text-xs h-8"><Plus className="w-3 h-3" /></Button>
        </div>
      </div>
      <div>
        <h3 className="text-xs font-bold mb-2" style={{ color: "#f7c935" }}>Categories</h3>
        <ConfigList items={categories} onRemove={removeCat} addVal={newCat} setAddVal={setNewCat} onAdd={addCat} placeholder="Nouvelle categorie" />
      </div>
      <div>
        <h3 className="text-xs font-bold mb-2" style={{ color: "#f7c935" }}>Statuts</h3>
        <ConfigList items={statuses} onRemove={removeStat} addVal={newStat} setAddVal={setNewStat} onAdd={addStat} placeholder="Nouveau statut" showColor />
      </div>
      <div>
        <h3 className="text-xs font-bold mb-2" style={{ color: "#f7c935" }}>Comptes bancaires</h3>
        <ConfigList items={banques} onRemove={removeBanque} addVal={newBanque} setAddVal={setNewBanque} onAdd={addBanque} placeholder="Nouveau compte" />
      </div>
      <div>
        <h3 className="text-xs font-bold mb-2" style={{ color: "#f7c935" }}>Moyens de paiement</h3>
        <ConfigList items={moyens} onRemove={removeMoyen} addVal={newMoyen} setAddVal={setNewMoyen} onAdd={addMoyen} placeholder="Nouveau moyen" />
      </div>
      <Button onClick={save} disabled={saving} className="w-full bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="save-dep-config-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />} Enregistrer la configuration
      </Button>
    </div>
  );
}